import React, { useState, useEffect, useRef, useCallback } from 'react'
import "../css/Createpost.css"; // reuse same styles
import '../css/MomentViewer.css'
import '../css/CreateMomentMobile.css'
import Cropper from 'react-easy-crop'
import { Rnd } from 'react-rnd'
import { SketchPicker } from 'react-color'
import { v4 as uuidv4 } from 'uuid'
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const bBoxWidth = Math.abs(Math.cos(rotRad) * image.width) + Math.abs(Math.sin(rotRad) * image.height);
  const bBoxHeight = Math.abs(Math.sin(rotRad) * image.width) + Math.abs(Math.cos(rotRad) * image.height);

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.translate(-pixelCrop.x, -pixelCrop.y);
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  return await new Promise((resolve) => {
    canvas.toBlob((file) => {
      resolve(file);
    }, 'image/jpeg');
  });
}

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });
}

export default function CreateMoment() {
  const [files, setFiles] = useState([]);
  const [urls, setUrls] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [textOverlaysMap, setTextOverlaysMap] = useState({});
  const [selectedOverlayId, setSelectedOverlayId] = useState(null);
  const [previewUrls, setPreviewUrls] = useState({});
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraFacing, setCameraFacing] = useState('user');
  const [cameraStream, setCameraStream] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordElapsed, setRecordElapsed] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const cameraVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordIntervalRef = useRef(null);
  const addedAudioTracksRef = useRef([]);
  const [stickers, setStickers] = useState([]);
  const [drawing, setDrawing] = useState(false);
  const [color, setColor] = useState('#ffffff');
  const [cropMode, setCropMode] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const drawCanvasRef = useRef(null);
  const imgRef = useRef(null);
  const previewRef = useRef(null);
  const [orientationAngle, setOrientationAngle] = useState(0);

  // Helpers for device orientation and drawing
  const getOrientationAngle = () => {
    try {
      const sc = (typeof window !== 'undefined' && window.screen) ? window.screen : null;
      const so = (sc && sc.orientation && typeof sc.orientation.angle === 'number') ? sc.orientation.angle : null;
      if (so !== null) return ((so % 360) + 360) % 360;
      if (typeof window.orientation === 'number') return ((window.orientation % 360) + 360) % 360;
    } catch (e) {}
    return 0;
  };

  useEffect(() => {
    const upd = () => setOrientationAngle(getOrientationAngle());
    upd();
    window.addEventListener('orientationchange', upd);
    window.addEventListener('resize', upd);
    return () => { window.removeEventListener('orientationchange', upd); window.removeEventListener('resize', upd); };
  }, []);

  const drawVideoFrameToCtx = (videoEl, ctx, destW, destH, angle = 0, mirror = false) => {
    // Draw around the center so rotation and mirroring behave consistently
    try {
      const rad = (angle * Math.PI) / 180;
      ctx.save();
      ctx.translate(destW / 2, destH / 2);
      ctx.rotate(rad);
      if (mirror) ctx.scale(-1, 1);
      ctx.drawImage(videoEl, -destW / 2, -destH / 2, destW, destH);
      ctx.restore();
    } catch (e) {
      console.error('drawVideoFrameToCtx error', e);
      try { ctx.restore(); } catch (er) {}
    }
  };

  const notifyA = (msg) => toast.error(msg)
  const notifyB = (msg) => toast.success(msg)

  useEffect(() => {
    if (urls.length > 0) {
      fetch('/moments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + localStorage.getItem('jwt')
        },
        body: JSON.stringify({ media: urls })
      }).then(r => r.json()).then(data => {
        if (data.error) {
          notifyA(data.error);
        } else {
          notifyB('Moment created');
          navigate('/');
        }
      });
    }
  }, [urls, navigate]);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const applyCropToSelected = async () => {
    const f = files[selectedIndex];
    if (!f) return;
    const src = URL.createObjectURL(f);
    const blob = await getCroppedImg(src, croppedAreaPixels, rotation);
    const editedFile = new File([blob], f.name || `edited.jpg`, { type: 'image/jpeg' });
    const newFiles = [...files];
    newFiles[selectedIndex] = Object.assign(f, { editedBlob: editedFile });
    setFiles(newFiles);
    setCropMode(false);
  };

  const loadfile = async (event) => {
    const selected = Array.from(event.target.files);
    for (const f of selected) {
      if (f.type && f.type.startsWith('video/')) {
        const url = URL.createObjectURL(f);
        const video = document.createElement('video');
        video.src = url;
        await new Promise((resolve) => {
          video.onloadedmetadata = () => {
            if (video.duration > 60) {
              notifyA('Videos must be 60s or shorter');
              const idx = selected.indexOf(f);
              if (idx !== -1) selected.splice(idx,1);
            }
            resolve();
          };
          video.onerror = () => { resolve(); };
        });
      }
    }
    if (selected.length + files.length > 50) {
      notifyA('Maximum 50 items allowed overall');
      return;
    }
    setFiles(prev => [...prev, ...selected]);
  };

  const addText = () => {
    const id = uuidv4();
    setTextOverlaysMap(prev => {
      const arr = prev[selectedIndex] ? [...prev[selectedIndex]] : [];
      arr.push({ id, text: 'Edit me', x: 30, y: 30, width: 140, height: 40, fontSize: 28, color: '#ffffff', editing: true, rotation: 0 });
      setSelectedOverlayId(id);
      return { ...prev, [selectedIndex]: arr };
    });
  };

  const updateText = (id, patch) => {
    setTextOverlaysMap(prev => {
      const arr = (prev[selectedIndex] || []).map(t => t.id === id ? { ...t, ...patch } : t);
      return { ...prev, [selectedIndex]: arr };
    });
  };

  const setEditing = (id, val) => {
    setTextOverlaysMap(prev => {
      const arr = (prev[selectedIndex] || []).map(t => t.id === id ? { ...t, editing: val } : t);
      return { ...prev, [selectedIndex]: arr };
    });
  };

  const setColorForSelected = (hex) => {
    if (!selectedOverlayId) return;
    setTextOverlaysMap(prev => {
      const arr = (prev[selectedIndex] || []).map(t => t.id === selectedOverlayId ? { ...t, color: hex } : t);
      return { ...prev, [selectedIndex]: arr };
    });
  };

  const deleteCurrentSlide = () => {
    if (!files[selectedIndex]) return;
    const newFiles = [...files];
    newFiles.splice(selectedIndex, 1);
    setFiles(newFiles);
    // remove overlays for that slide
    setTextOverlaysMap(prev => {
      const copy = { ...prev };
      // shift overlays map keys
      const newMap = {};
      for (let i = 0; i < newFiles.length; i++) {
        newMap[i] = copy[i < selectedIndex ? i : i+1] || [];
      }
      return newMap;
    });
    setSelectedIndex(i => Math.max(0, i - 1));
  };

  /* Camera capture: open camera stream and allow photo or 60s video capture */
  const openCamera = async (facing) => {
    setProcessing(true); setProcessingMessage('Opening camera...');
    try {
      const desired = facing || cameraFacing || 'user';
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: desired }, audio: true });
      // stop any existing stream first
      if (cameraStream) {
        try { cameraStream.getTracks().forEach(t => t.stop()); } catch(e){}
      }
      setCameraFacing(desired);
      setCameraStream(s);
      setShowCamera(true);
      if (cameraVideoRef.current) cameraVideoRef.current.srcObject = s;
    } catch (e) { 
      console.error('Camera error:', e);
      if (e.name === 'NotAllowedError') {
        notifyA('Camera permission denied. Please allow camera access in browser settings.');
      } else if (e.name === 'NotFoundError') {
        notifyA('No camera device found on this device.');
      } else if (e.name === 'NotSupportedError') {
        notifyA('Camera access requires HTTPS connection. Please reconnect via HTTPS.');
      } else {
        notifyA('Camera error: ' + e.message);
      }
    } finally {
      setProcessing(false); setProcessingMessage('');
    }
  };

  const toggleCameraFacing = async () => {
    const newFacing = cameraFacing === 'user' ? 'environment' : 'user';
    setProcessing(true); setProcessingMessage('Switching camera...');
    try {
      // Always request audio so new stream has mic attached
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: newFacing }, audio: true });
      const newVideoTrack = s.getVideoTracks()[0];

      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording' && cameraStream) {
        // Replace video tracks on the existing stream so MediaRecorder keeps recording
        try {
          const oldVideoTracks = cameraStream.getVideoTracks();
          oldVideoTracks.forEach(t => { try { cameraStream.removeTrack(t); t.stop(); } catch(e){} });
          if (newVideoTrack) cameraStream.addTrack(newVideoTrack);
          // keep existing audio tracks as-is
          if (cameraVideoRef.current) cameraVideoRef.current.srcObject = cameraStream;
          setCameraFacing(newFacing);
        } catch (e) {
          console.error('Swap tracks failed', e);
          notifyA('Unable to switch camera while recording');
        }
        // stop any leftover tracks from the temporary stream except the one we added
        s.getTracks().forEach(t => { if (t !== newVideoTrack) try { t.stop(); } catch(e){} });
      } else {
        // Not recording: fully replace the stream
        if (cameraStream) { try { cameraStream.getTracks().forEach(t => t.stop()); } catch(e){} }
        setCameraFacing(newFacing);
        setCameraStream(s);
        if (cameraVideoRef.current) cameraVideoRef.current.srcObject = s;
      }
    } catch (e) {
      notifyA('Unable to switch camera: ' + (e.message || e.name));
    } finally {
      setProcessing(false); setProcessingMessage('');
    }
  };

  // ensure video element displays current stream
  useEffect(() => {
    if (cameraVideoRef.current && cameraStream) {
      try { cameraVideoRef.current.srcObject = cameraStream; } catch(e) {}
    }
    return () => {};
  }, [cameraStream]);

  // update camera preview transform for orientation and mirroring
  useEffect(() => {
    if (!cameraVideoRef.current) return;
    const el = cameraVideoRef.current;
    const mirror = cameraFacing === 'user';
    const ang = orientationAngle || 0;
    // apply rotation and mirror
    el.style.transform = `rotate(${ang}deg) ${mirror ? 'scaleX(-1)' : ''}`;
    el.style.objectFit = 'cover';
  }, [cameraFacing, orientationAngle, cameraStream]);

  // cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (recordIntervalRef.current) clearInterval(recordIntervalRef.current);
    };
  }, []);

  const closeCamera = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    if (recordIntervalRef.current) { clearInterval(recordIntervalRef.current); recordIntervalRef.current = null; }
    setRecordElapsed(0);
    setRecording(false);
    if (cameraStream) {
      cameraStream.getTracks().forEach(t => t.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  const takePhoto = async () => {
    if (!cameraVideoRef.current) {
      notifyA('Camera not ready');
      return;
    }
    const video = cameraVideoRef.current;
    
    // Ensure video has loaded metadata
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      notifyA('Camera video not ready, please wait');
      return;
    }
    
    try {
      const angle = getOrientationAngle();
      const mirror = cameraFacing === 'user';
      // determine canvas size (swap for 90/270)
      const swap = angle === 90 || angle === 270;
      const vw = video.videoWidth || 640;
      const vh = video.videoHeight || 480;
      const canvas = document.createElement('canvas');
      canvas.width = swap ? vh : vw;
      canvas.height = swap ? vw : vh;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        notifyA('Failed to get canvas context');
        return;
      }
      // draw respecting device orientation and mirror for front camera
      drawVideoFrameToCtx(video, ctx, canvas.width, canvas.height, angle, mirror);

      canvas.toBlob((blob) => {
        if (!blob) {
          notifyA('Failed to create blob');
          return;
        }
        const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
        const newFiles = [...files, file];
        setFiles(newFiles);
        setSelectedIndex(newFiles.length - 1);
        notifyB('Photo captured');
        // close camera after short delay for UX
        setTimeout(() => closeCamera(), 300);
      }, 'image/jpeg', 0.95);
    } catch (e) {
      notifyA('Photo capture failed: ' + e.message);
    }
  };

  const startRecording = async () => {
    if (!cameraStream) return;
    recordedChunksRef.current = [];
    const useCanvasRecording = cameraFacing === 'user' || getOrientationAngle() !== 0;

    const startTimer = () => {
      setRecording(true);
      setRecordElapsed(0);
      recordIntervalRef.current = setInterval(() => {
        setRecordElapsed(prev => {
          const next = prev + 0.5; // update every 500ms
          if (next >= 60) {
            try { if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop(); } catch(e){}
            return 60;
          }
          return next;
        });
      }, 500);
    };

    if (useCanvasRecording) {
      // Render camera stream into a hidden video element and draw frames to canvas
      const hiddenV = document.createElement('video');
      hiddenV.autoplay = false;
      hiddenV.muted = true;
      hiddenV.playsInline = true;
      hiddenV.srcObject = cameraStream;
      const onLoaded = () => {
        const vw = hiddenV.videoWidth || 640;
        const vh = hiddenV.videoHeight || 480;
        const swap = getOrientationAngle() === 90 || getOrientationAngle() === 270;
        const canvas = document.createElement('canvas');
        canvas.width = swap ? vh : vw;
        canvas.height = swap ? vw : vh;
        const ctx = canvas.getContext('2d');
        const raf = { id: null };

        const drawLoop = () => {
          try {
            const angle = getOrientationAngle();
            drawVideoFrameToCtx(hiddenV, ctx, canvas.width, canvas.height, angle, cameraFacing === 'user');
          } catch (e) { console.error('drawLoop', e); }
          raf.id = requestAnimationFrame(drawLoop);
        };

        const canvasStream = canvas.captureStream(30);
        const combined = new MediaStream();
        const vTrack = canvasStream.getVideoTracks()[0];
        if (vTrack) combined.addTrack(vTrack);
        // attach audio tracks from cameraStream if available; fallback to AudioContext if needed
        try {
          let camAudioTracks = cameraStream.getAudioTracks() || [];
          if (!camAudioTracks.length) {
            try {
              const AC = window.AudioContext || window.webkitAudioContext;
              if (AC) {
                const ac = new AC();
                const src = ac.createMediaStreamSource(cameraStream);
                const dest = ac.createMediaStreamDestination();
                src.connect(dest);
                camAudioTracks = dest.stream.getAudioTracks() || [];
              }
            } catch (e) { camAudioTracks = []; }
          }
          camAudioTracks.forEach(t => {
            try { const ct = t.clone ? t.clone() : t; ct.enabled = true; combined.addTrack(ct); } catch(e) { try { t.enabled = true; combined.addTrack(t); } catch(_) {} }
          });
        } catch (e) {}

        let options = { mimeType: 'video/webm;codecs=vp9' };
        if (!MediaRecorder.isTypeSupported(options.mimeType)) options = { mimeType: 'video/webm' };
        const mr = new MediaRecorder(combined, options);
        mediaRecorderRef.current = mr;
        mr.ondataavailable = (ev) => { if (ev.data && ev.data.size) recordedChunksRef.current.push(ev.data); };
        mr.onstop = () => {
          if (raf.id) cancelAnimationFrame(raf.id);
          const blob = new Blob(recordedChunksRef.current, { type: recordedChunksRef.current.length ? recordedChunksRef.current[0].type || 'video/webm' : 'video/webm' });
          if (!blob || blob.size === 0) {
            console.error('Recorded blob empty');
            notifyA('Recording failed: empty data');
            setRecording(false);
            setRecordElapsed(0);
            if (recordIntervalRef.current) { clearInterval(recordIntervalRef.current); recordIntervalRef.current = null; }
            setTimeout(() => closeCamera(), 300);
            return;
          }
          const file = new File([blob], `camera-${Date.now()}.webm`, { type: blob.type });
          const newFiles = [...files, file];
          setFiles(newFiles);
          setSelectedIndex(newFiles.length - 1);
          setRecording(false);
          setRecordElapsed(0);
          if (recordIntervalRef.current) { clearInterval(recordIntervalRef.current); recordIntervalRef.current = null; }
          setTimeout(() => closeCamera(), 300);
        };

        try {
          mr.start();
          hiddenV.play().catch(() => {});
          drawLoop();
          startTimer();
          // recording started
          setProcessing(false); setProcessingMessage('');
        } catch (e) {
          console.error('Canvas MediaRecorder start error', e);
          setProcessing(false); setProcessingMessage('');
          notifyA('Recording failed: ' + (e.message || e.name));
        }
      };

      hiddenV.addEventListener('loadedmetadata', onLoaded, { once: true });
      // attempt to load
      hiddenV.play().catch(() => {});
    } else {
      // plain MediaRecorder on cameraStream
      let options = { mimeType: 'video/webm;codecs=vp9' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'video/webm' };
      }
      try {
        // Ensure cameraStream has audio tracks. If not, request mic and attach its tracks to cameraStream so MediaRecorder records audio.
        addedAudioTracksRef.current = [];
        try {
          const existingAudio = cameraStream.getAudioTracks() || [];
          if (!existingAudio.length) {
            const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const micTracks = micStream.getAudioTracks() || [];
            micTracks.forEach(t => {
              try { cameraStream.addTrack(t); addedAudioTracksRef.current.push(t); } catch(e) {}
            });
          }
        } catch (e) { /* ignore mic attach errors */ }

        const mr = new MediaRecorder(cameraStream, options);
        mediaRecorderRef.current = mr;
        mr.ondataavailable = (ev) => { if (ev.data && ev.data.size) recordedChunksRef.current.push(ev.data); };
        mr.onstop = () => {
          const blob = new Blob(recordedChunksRef.current, { type: recordedChunksRef.current.length ? recordedChunksRef.current[0].type || 'video/webm' : 'video/webm' });
          if (!blob || blob.size === 0) {
            console.error('Recorded blob empty');
            notifyA('Recording failed: empty data');
            setRecording(false);
            setRecordElapsed(0);
            if (recordIntervalRef.current) { clearInterval(recordIntervalRef.current); recordIntervalRef.current = null; }
            try { addedAudioTracksRef.current.forEach(t => { try { t.stop(); cameraStream.removeTrack(t); } catch(_) {} }); addedAudioTracksRef.current = []; } catch(_) {}
            setTimeout(() => closeCamera(), 300);
            return;
          }
          const file = new File([blob], `camera-${Date.now()}.webm`, { type: blob.type });
          const newFiles = [...files, file];
          setFiles(newFiles);
          setSelectedIndex(newFiles.length - 1);
          // reset recording state
          setRecording(false);
          setRecordElapsed(0);
          if (recordIntervalRef.current) { clearInterval(recordIntervalRef.current); recordIntervalRef.current = null; }
          // stop any temporarily added audio tracks
          try { addedAudioTracksRef.current.forEach(t => { try { t.stop(); cameraStream.removeTrack(t); } catch(_) {} }); addedAudioTracksRef.current = []; } catch(_) {}
          // close camera after short delay for UX
          setTimeout(() => closeCamera(), 300);
        };
        mr.start();
        startTimer();
      } catch (e) {
        console.error('MediaRecorder start error', e);
        notifyA('Recording failed: ' + e.message);
      }
    }
  };

  const stopRecording = () => {
    try { if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') mediaRecorderRef.current.stop(); } catch(e){}
  };

  const removeText = (id) => {
    setTextOverlaysMap(prev => {
      const arr = (prev[selectedIndex] || []).filter(t => t.id !== id);
      return { ...prev, [selectedIndex]: arr };
    });
  };

  const toggleDrawing = () => {
    setDrawing(d => !d);
  };

  const applyEditsToSelected = async () => {
    const f = files[selectedIndex];
    if (!f) return;
    if (f.type && f.type.startsWith('video/')) {
      // For videos: do not bake overlays into the video on apply. Keep overlays as a layer
      // so preview shows the text immediately and upload will include overlays as metadata.
      // Ensure a preview URL exists for this video so overlays render over it.
      try {
        setPreviewUrls(prev => {
          if (prev[selectedIndex]) return prev;
          try { if (files[selectedIndex]) return { ...prev, [selectedIndex]: URL.createObjectURL(files[selectedIndex]) }; } catch(e) {}
          return prev;
        });
        notifyB('Text applied to video slide (layered preview).');
      } catch (e) {
        console.error('Apply overlays to video failed', e);
        notifyA('Failed to apply overlays to video preview.');
      }
      return;
    }
    // composite image, drawings, stickers, texts onto a canvas
    const imgSrc = f.editedBlob ? URL.createObjectURL(f.editedBlob) : URL.createObjectURL(f);
    const image = await createImage(imgSrc);
    const canvas = document.createElement('canvas');
    canvas.width = image.width; canvas.height = image.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0);
    // draw text overlays mapping from container coords -> image natural coords
    const overlays = textOverlaysMap[selectedIndex] || [];
    const containerRect = previewRef.current?.getBoundingClientRect();
    const containerWidth = containerRect?.width || previewRef.current?.clientWidth || image.width;
    const containerHeight = containerRect?.height || previewRef.current?.clientHeight || image.height;
    // For object-fit:contain, the image is scaled to fit and centered; compute scale and offsets
    const scale = Math.min(containerWidth / image.width, containerHeight / image.height) || 1;
    const scaledImageWidth = image.width * scale;
    const scaledImageHeight = image.height * scale;
    const offsetX = (containerWidth - scaledImageWidth) / 2;
    const offsetY = (containerHeight - scaledImageHeight) / 2;
    for (const t of overlays) {
      // Map container coordinates (Rnd) to image natural pixels
      const containerX = (t.x || 0);
      const containerY = (t.y || 0);
      const containerW = (t.width || 140);
      const containerH = (t.height || 40);
      const imgPxX = (containerX - offsetX) / scale;
      const imgPxY = (containerY - offsetY) / scale;
      const imgBoxW = containerW / scale;
      const imgBoxH = containerH / scale;

      const baseFont = t.fontSize || 20;
      const fontPx = Math.max(6, Math.round(baseFont / scale));
      ctx.fillStyle = t.color || '#fff';
      ctx.textBaseline = 'top';
      ctx.font = `${fontPx}px sans-serif`;

      // Wrap text into lines that fit imgBoxW
      const words = (t.text || '').split(/\s+/);
      const lines = [];
      let cur = '';
      for (let w of words) {
        const test = cur ? (cur + ' ' + w) : w;
        const measure = ctx.measureText(test).width;
        if (measure <= imgBoxW - 8) { // small padding
          cur = test;
        } else {
          if (cur) lines.push(cur);
          cur = w;
        }
      }
      if (cur) lines.push(cur);

      const lineHeight = Math.round(fontPx * 1.15);
      const textHeight = lines.length * lineHeight;
      // draw centered inside the image-mapped box, supporting rotation
      const angle = (t.rotation || 0) * Math.PI / 180;
      const imgCenterX = imgPxX + imgBoxW / 2;
      const imgCenterY = imgPxY + imgBoxH / 2;
      ctx.save();
      ctx.translate(imgCenterX, imgCenterY);
      ctx.rotate(angle);
      // startY relative to center
      const startY = -textHeight / 2;
      for (let li = 0; li < lines.length; li++) {
        const line = lines[li];
        const w = ctx.measureText(line).width;
        const drawX = -w / 2;
        const drawY = startY + li * lineHeight;
        ctx.fillText(line, drawX, drawY);
      }
      ctx.restore();
    }
    // draw drawing canvas if present
    if (drawCanvasRef.current) {
      ctx.drawImage(drawCanvasRef.current, 0, 0, canvas.width, canvas.height);
    }
    const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.9));
    const editedFile = new File([blob], f.name || `edited.jpg`, { type: 'image/jpeg' });
    const newFiles = [...files];
    // attach editedBlob property
    newFiles[selectedIndex] = Object.assign(f, { editedBlob: editedFile });
    setFiles(newFiles);
    // create preview URL for immediate rendering and clean up previous
    setPreviewUrls(prev => {
      try { if (prev[selectedIndex]) URL.revokeObjectURL(prev[selectedIndex]); } catch(e) {}
      return { ...prev, [selectedIndex]: URL.createObjectURL(editedFile) };
    });
    // Clear editor overlays for this slide because they are now baked into the image
    setTextOverlaysMap(prev => ({ ...prev, [selectedIndex]: [] }));
    setSelectedOverlayId(null);
    notifyB('Edits applied to image');
  };

  const uploadAll = async () => {
    if (files.length === 0) { notifyA('Select at least one file'); return; }
    setUploading(true);
    const uploaded = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const data = new FormData();
        if (file.editedBlob) data.append('file', file.editedBlob, file.name || 'edited.jpg');
        else data.append('file', file);
        data.append('upload_preset', 'linkalikes');
        data.append('cloud_name','linkalikescloud');
        const isVideo = file.type && file.type.startsWith('video/');
        const urlBase = isVideo ?
          'https://api.cloudinary.com/v1_1/linkalikescloud/video/upload' :
          'https://api.cloudinary.com/v1_1/linkalikescloud/image/upload';
        const res = await fetch(urlBase, { method: 'post', body: data });
        const result = await res.json();
        if (result.error) throw result.error;
        // If the image was baked (editedBlob), the overlays are already applied to the image
        // so don't send them again as metadata to avoid duplicate rendering.
        const containerRect = previewRef.current?.getBoundingClientRect();
        const containerWidth = containerRect?.width || window.innerWidth || 1;
        const containerHeight = containerRect?.height || window.innerHeight || 1;
        const overlays = file.editedBlob ? [] : (textOverlaysMap[i] || []).map(t => ({
          text: t.text,
          x: t.x,
          y: t.y,
          xPct: (t.x || 0) / containerWidth,
          yPct: (t.y || 0) / containerHeight,
          sizePct: (t.fontSize || 18) / containerWidth,
          size: t.fontSize,
          color: t.color
        }));
        uploaded.push({ url: result.secure_url || result.url, type: isVideo ? 'video' : 'image', overlays });
      }
      setUrls(uploaded);
    } catch (e) { 
      console.error(e); 
      notifyA('Upload failed'); 
      setUploading(false);
    }
  };

  const fileInputRef = useRef(null);

  const openFilePicker = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const moveSlide = (dir) => {
    // dir = -1 (left) or 1 (right)
    const i = selectedIndex;
    const j = i + dir;
    if (j < 0 || j >= files.length) return;
    const newFiles = [...files];
    const tmp = newFiles[i]; newFiles[i] = newFiles[j]; newFiles[j] = tmp;
    // reorder overlays map accordingly
    setTextOverlaysMap(prev => {
      const copy = { ...prev };
      const newMap = {};
      for (let k = 0; k < newFiles.length; k++) {
        // old index of current newFiles[k]
        const oldIdx = (k === j ? i : (k === i ? j : k));
        newMap[k] = copy[oldIdx] || [];
      }
      return newMap;
    });
    setFiles(newFiles);
    setSelectedIndex(j);
    // also swap previewUrls
    setPreviewUrls(prev => {
      const p = { ...prev };
      const tmpu = p[i]; p[i] = p[j]; p[j] = tmpu;
      return p;
    });
  };

  const handlePreviewTap = (e) => {
    // Allow clicks on the actual <video> element to behave normally (play/pause/seek).
    // Only interpret taps for navigation when the click wasn't on the video element.
    try {
      if (!previewRef.current) return;
      const tgt = e.target;
      // ignore clicks on actual video element (so native controls work)
      if (tgt && (tgt.tagName === 'VIDEO' || (tgt.closest && tgt.closest('video')))) {
        // let native video controls handle the interaction
        return;
      }
      // ignore clicks on overlays / editable text so editing isn't interrupted
      if (tgt && tgt.closest && (tgt.closest('.react-rnd') || tgt.closest('.mv-text-item') || tgt.closest('.react-rnd'))) {
        return;
      }
      const rect = previewRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      if (x < rect.width / 2) {
        if (selectedIndex > 0) setSelectedIndex(selectedIndex - 1);
      } else {
        if (selectedIndex < files.length - 1) setSelectedIndex(selectedIndex + 1);
      }
    } catch (err) { console.error('preview tap handler', err); }
  };

  return (
    <div className="create-moment-mobile">
      <div className="cm-top">
        <button className="cm-btn cm-small" onClick={() => navigate(-1)} disabled={uploading}>Close</button>
        <div className="title">Create Moment</div>
        <button className="cm-btn cm-small" onClick={uploadAll} disabled={uploading}>{uploading ? '⏳ Uploading...' : 'Share'}</button>
      </div>

      <div className="cm-preview" ref={previewRef} onClick={handlePreviewTap}>
        {files[selectedIndex] ? (
          files[selectedIndex].type && files[selectedIndex].type.startsWith('image/') ? (
            <img ref={imgRef} src={files[selectedIndex].editedBlob ? (previewUrls[selectedIndex] || URL.createObjectURL(files[selectedIndex].editedBlob)) : URL.createObjectURL(files[selectedIndex])} alt="preview" />
          ) : (
                <video src={previewUrls[selectedIndex] || URL.createObjectURL(files[selectedIndex])} controls />
          )
        ) : (
          <div style={{color:'#888'}}>Tap "Add" to add photos or videos</div>
        )}
        {/* show text overlays for the selected slide (draggable/resizable) */}
        {(textOverlaysMap[selectedIndex] || []).map((t) => (
          <Rnd
            key={t.id}
            size={{ width: t.width || 140, height: t.height || 40 }}
            position={{ x: t.x || 30, y: t.y || 30 }}
            bounds="parent"
            disableDragging={!!t.editing}
            onDragStop={(e, d) => updateText(t.id, { x: d.x, y: d.y })}
            onResizeStop={(e, direction, ref, delta, pos) => {
              const newW = ref.offsetWidth;
              const newH = ref.offsetHeight;
              const newFont = Math.max(12, Math.round(newH * 0.45));
              updateText(t.id, { width: newW, height: newH, x: pos.x, y: pos.y, fontSize: newFont });
            }}
            enableResizing={!t.editing}
            style={{ zIndex: selectedOverlayId === t.id ? 2300 : 2200 }}
            onClick={() => setSelectedOverlayId(t.id)}
          >
            <div
              className="mv-text-item"
              style={{ width: '100%', height: '100%', fontSize: t.fontSize, color: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: t.editing ? 'text' : 'grab', padding: '4px 6px', outline: selectedOverlayId === t.id && !t.editing ? '2px dashed rgba(255,255,255,0.7)' : 'none', outlineOffset: '2px', transform: `rotate(${t.rotation || 0}deg)`, transformOrigin: 'center' }}
              contentEditable={!!t.editing}
              suppressContentEditableWarning
              onBlur={(e) => { updateText(t.id, { text: e.target.innerText }); setEditing(t.id, false); }}
              onDoubleClick={(e) => { e.stopPropagation(); setEditing(t.id, true); e.currentTarget.focus(); }}
              onKeyDown={(e) => { if (e.key === 'Delete' && t.editing) { e.preventDefault(); removeText(t.id); } }}
              onClick={(e) => { e.stopPropagation(); if (!t.editing) setSelectedOverlayId(t.id); }}
              tabIndex={0}
            >
              {t.text}
            </div>
          </Rnd>
        ))}
      </div>

      {processing && (
        <div className="cm-processing-overlay">
          <div className="cm-processing-box">
            <div className="spinner" />
            <div className="processing-message">{processingMessage || 'Processing...'}</div>
          </div>
        </div>
      )}

      <div className="cm-thumb-scroll">
        {files.map((f, i) => (
          <img key={i} className="cm-thumb" src={f.type && f.type.startsWith('image/') ? (previewUrls[i] || (f.editedBlob ? URL.createObjectURL(f.editedBlob) : URL.createObjectURL(f))) : '/logo192.png'} onClick={() => setSelectedIndex(i)} alt="thumb" />
        ))}
      </div>

      <div className="cm-toolbar">
        <input ref={fileInputRef} className="cm-file-input" type="file" accept="image/*,video/*" multiple onChange={loadfile} />
        <button className="cm-btn" onClick={openFilePicker} title="Add files" aria-label="Add files">＋</button>
        <button className="cm-btn" onClick={openCamera}>📷</button>
        {files.length > 1 && (
          <>
            <button className="cm-btn" onClick={() => moveSlide(-1)} disabled={selectedIndex === 0} title="Move to previous" aria-label="Move to previous">←</button>
            <button className="cm-btn" onClick={() => moveSlide(1)} disabled={selectedIndex === files.length - 1} title="Move to next" aria-label="Move to next">→</button>
          </>
        )}
        <button className="cm-btn" onClick={addText} title="Add text" aria-label="Add text">T</button>
        <button className="cm-btn" onClick={applyEditsToSelected} disabled={uploading}>✓</button>
        {files.length > 1 && <button className="cm-btn" style={{color:'#ff4444'}} onClick={deleteCurrentSlide}>× Slide</button>}
        {selectedOverlayId && <button className="cm-btn" onClick={() => setShowColorPicker(s => !s)}>🎨</button>}
        {selectedOverlayId && <button className="cm-btn" onClick={() => updateText(selectedOverlayId, { rotation: ((textOverlaysMap[selectedIndex]||[]).find(t=>t.id===selectedOverlayId)?.rotation||0) - 15 })}>⤾</button>}
        {selectedOverlayId && <button className="cm-btn" onClick={() => updateText(selectedOverlayId, { rotation: ((textOverlaysMap[selectedIndex]||[]).find(t=>t.id===selectedOverlayId)?.rotation||0) + 15 })}>⤻</button>}
        {selectedOverlayId && <button className="cm-btn" style={{color:'#ff4444'}} onClick={() => removeText(selectedOverlayId)}>× Text</button>}
      </div>

      {showColorPicker && selectedOverlayId && (
        <div style={{position:'fixed',bottom:90,right:12,zIndex:2400,boxShadow:'0 0 10px rgba(0,0,0,0.5)',borderRadius:'8px'}}>
          <SketchPicker color={(textOverlaysMap[selectedIndex] || []).find(t => t.id === selectedOverlayId)?.color || '#ffffff'} onChangeComplete={(c) => setColorForSelected(c.hex)} />
        </div>
      )}

      {showCamera && (
        <div className="cm-camera-fullscreen">
          <div className="cm-camera-frame">
            <div className="cm-camera-preview">
              <video ref={cameraVideoRef} autoPlay playsInline muted className="camera-video-el" />
              <div className="camera-top-bar">
                <div className="camera-progress">
                  <div className="seg" style={{width: '20%'}}></div>
                  <div className="seg" style={{width: '20%'}}></div>
                  <div className="seg" style={{width: '20%'}}></div>
                  <div className="seg" style={{width: '20%'}}></div>
                  <div className="seg" style={{width: '20%'}}></div>
                </div>
              </div>

              <div className="camera-record-controls">
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <div onClick={takePhoto} style={{borderRadius: "80px", height: "72px", width: "72px", background: "white", boxShadow: "inset 0 0 0 3px black", border: "4px solid white"}}>

                  </div>
                  <div
                    className={"record-btn " + (recording ? 'recording' : '')}
                    onClick={() => { recording ? stopRecording() : startRecording(); }}
                    title={recording ? 'Stop' : 'Record'}
                  >
                    <div style={{width: recording ? 28 : 36, height: recording ? 28 : 36, background: recording ? '#fff' : '#fff', borderRadius: recording ? 6 : '50%'}} />
                  </div>
                  <div className="record-timer">{Math.floor(recordElapsed)}s / 60s</div>
                </div>
              </div>

              <div className="camera-side-btns">
                {/* <div className="cam-small" onClick={takePhoto} title="Capture photo">📸</div> */}
                <div className="cam-small" onClick={toggleCameraFacing} title="Flip camera">🔁</div>
                <div className="cam-small" onClick={closeCamera} title="Close">✕</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
